import { useTheme } from '@nfinite/react-hubstairs/styled/theme'
import { transparentize } from 'polished'
import { useDrag } from 'react-use-gesture'
import ReactPortal from 'components/Commons/ReactPortal'
import useFreezeBody from 'hooks/useFreezeBody'

import Cross from 'assets/svg/Cross.svg?react'

import { Bottom, Left, Right } from './styled'
import { AnimatePresence } from 'framer-motion'

const CLOSE_ANIMATION_TIME = 0.5

export const DRAWER_TYPES = {
  BOTTOM: 'slides from the bottom',
  LEFT: 'slides from the left',
  RIGHT: 'slides from the right',
}

function getComponentByOrientation(orientation) {
  switch (orientation) {
    case DRAWER_TYPES.LEFT:
      return Left
    case DRAWER_TYPES.RIGHT:
      return Right
    case DRAWER_TYPES.BOTTOM:
      return Bottom
    default:
      return null
  }
}

function Drawer({ id, open, setOpen, children, onClose, orientation, withCloseCross = true }) {
  useFreezeBody(open)

  function handleClose(e) {
    e.preventDefault()
    e.stopPropagation()
    if (onClose) onClose()
  }

  return (
    <ReactPortal id={id}>
      <AnimatedDrawer
        open={open}
        setOpen={setOpen}
        orientation={orientation}
        handleClose={handleClose}
        withCloseBar={orientation === DRAWER_TYPES.BOTTOM && !!onClose}
        withCloseCross={withCloseCross && [DRAWER_TYPES.LEFT, DRAWER_TYPES.RIGHT].includes(orientation) && !!onClose}
      >
        {children}
      </AnimatedDrawer>
    </ReactPortal>
  )
}

const variantsOverlay = {
  open: ({ theme }) => ({
    backgroundColor: transparentize(0.85, theme.getColor('background', 'contrast')),
    transition: {
      duration: 0.3,
      ease: [0.04, 0.62, 0.23, 0.98],
    },
  }),
  closed: ({ theme }) => ({
    backgroundColor: transparentize(1, theme.getColor('background', 'contrast')),
    transition: {
      duration: 0.5,
      ease: [0.04, 0.62, 0.23, 0.98],
    },
  }),
}

const variantsContainer = {
  open: ({ orientation }) => {
    const transition = {
      delay: 0.1,
      type: 'spring',
      stiffness: 800,
      damping: 100,
    }

    switch (orientation) {
      case DRAWER_TYPES.BOTTOM:
        return { y: 0, transition }
      case DRAWER_TYPES.RIGHT:
        return { x: 0, transition }
      case DRAWER_TYPES.LEFT:
      default:
        return { x: 0, transition }
    }
  },
  closed: ({ orientation }) => {
    const transition = {
      delay: 0.2,
      type: 'spring',
      stiffness: 800,
      damping: 100,
    }

    switch (orientation) {
      case DRAWER_TYPES.BOTTOM:
        return { y: '100%', transition }
      case DRAWER_TYPES.RIGHT:
        return { x: '200%', transition }
      case DRAWER_TYPES.LEFT:
      default:
        return { x: '-100%', transition }
    }
  },
}

function AnimatedDrawer({ open, setOpen, orientation, handleClose, withCloseBar, withCloseCross, children }) {
  const Component = getComponentByOrientation(orientation)
  const theme = useTheme()
  const bind = useDrag(({ dragging, down, elapsedTime, event }) => {
    const isSwipeDown = dragging && down && elapsedTime > 0
    if (isSwipeDown) {
      setOpen(false)
      setTimeout(
        () => {
          setOpen(true)
          handleClose(event)
        },
        CLOSE_ANIMATION_TIME * 1000,
        setOpen,
        handleClose,
        event,
      )
    }
  })

  return (
    <AnimatePresence initial={open} custom={{ orientation, theme }}>
      {open && (
        <Component.Overlay
          initial="closed"
          animate="open"
          exit="closed"
          custom={{ theme }}
          variants={variantsOverlay}
          onClick={handleClose}
        >
          <Component.Container
            initial="closed"
            animate="open"
            exit="closed"
            custom={{ orientation }}
            variants={variantsContainer}
            onClick={e => e.stopPropagation()}
          >
            {withCloseBar && <Component.CloseBar {...bind()} />}
            {withCloseCross && (
              <Component.CloseCross onClick={() => setOpen(false)}>
                <Component.CrossIcon component={Cross} />
              </Component.CloseCross>
            )}
            {children}
          </Component.Container>
        </Component.Overlay>
      )}
    </AnimatePresence>
  )
}

export default Drawer
